import React from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { InputNumberMask } from '../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { CharitableGivingOptions } from '../../../../../api/useGoalsQuery';
import { CharitableGoalDataDTO } from '../../../CharitableGiving.meta';
import { useFormUtils } from '../../../hooks/useFormUtils';
import { DafAccount } from '../../DafAccount/DafAccount';
import { KindOfProperty } from '../../KindOfProperty/KindOfProperty';
import { FundAccount } from '../../FundAccount/FundAccount';
import { AssetBucketingSelect } from '../../../../../components/AssetBucketingSelect/AssetBucketingSelect';

interface Props {
  options: CharitableGivingOptions;
  clientId: string;
  hasAssetBucketing: boolean;
  goalId: string | null;
}

export const DAF = ({ options, clientId, hasAssetBucketing, goalId }: Props) => {
  const { control, formState } = useFormContext<CharitableGoalDataDTO>();

  const { startYearOptionsWithAgeLabel, endYearOptionsWithAgeLabel } = useFormUtils({
    options,
  });

  const { dafStartYearOptions, dafEndYearOptions, donorOptions } = options;

  return (
    <>
      <Controller
        control={control}
        name="donor"
        render={({ field: { value: formValue, onChange, ...rest } }) => (
          <Select
            {...rest}
            isPortal
            label="Donor"
            value={donorOptions.find(({ value }) => value === formValue)}
            onChange={opt => onChange(opt?.value)}
            options={donorOptions}
            formVariant={formState.errors.donor ? 'error' : undefined}
            helperText={formState.errors.donor?.message}
          />
        )}
      />

      <DafAccount goalId={goalId} />

      <KindOfProperty options={options} />

      <Box
        display="grid"
        css={`
          grid-template-columns: 1fr 1fr;
          gap: 24px 16px;
        `}
      >
        <Controller
          control={control}
          name="dafDonationStartYear"
          render={({ field: { value, onChange, ...field } }) => (
            <Select
              {...field}
              isPortal
              label="Donation Start Year"
              value={dafStartYearOptions.find(
                opt => opt.value === value?.year && opt.type === value?.type,
              )}
              options={startYearOptionsWithAgeLabel}
              onChange={opt => {
                if (opt) {
                  onChange({ year: opt.value, type: opt.type });
                }
              }}
              formVariant={formState.errors.dafDonationStartYear ? 'error' : undefined}
              helperText={(formState.errors.dafDonationStartYear as FieldError)?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="dafDonationEndYear"
          render={({ field: { value, onChange, ...field } }) => (
            <Select
              {...field}
              isPortal
              label="Donation End Year"
              value={dafEndYearOptions.find(
                opt => opt.value === value?.year && opt.type === value?.type,
              )}
              options={endYearOptionsWithAgeLabel}
              onChange={opt => {
                if (opt) {
                  onChange({ year: opt.value, type: opt.type });
                }
              }}
              formVariant={formState.errors.dafDonationEndYear ? 'error' : undefined}
              helperText={(formState.errors.dafDonationEndYear as FieldError)?.message}
            />
          )}
        />
      </Box>

      <Controller
        control={control}
        name="donationAmount"
        render={({ field: { onChange, value, ...field } }) => (
          <InputNumberMask
            {...field}
            label="Donation Amount"
            leftIcon="fa fa-dollar-sign"
            innerText="/annually"
            value={value ?? ''}
            allowNegative={false}
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            formVariant={formState.errors.donationAmount ? 'error' : undefined}
            helperText={formState.errors.donationAmount?.message}
          />
        )}
      />

      <Box
        display="grid"
        css={`
          grid-template-columns: 1fr 1fr;
          gap: 24px 16px;
        `}
      >
        <Controller
          control={control}
          name="dafGrantStartYear"
          render={({ field: { value, onChange, ...field } }) => (
            <Select
              {...field}
              isPortal
              label={
                <Box display="flex" alignItems="baseline" style={{ gap: 4 }}>
                  <Text as="span" variant="formLabel">
                    Grant Start Year
                  </Text>
                  <Text as="span" fontSize="small" color="neutral.500">
                    (Optional)
                  </Text>
                </Box>
              }
              value={dafStartYearOptions.find(
                opt => opt.value === value?.year && opt.type === value?.type,
              )}
              options={startYearOptionsWithAgeLabel}
              onChange={opt => {
                if (opt) {
                  onChange({ year: opt.value, type: opt.type });
                }
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="dafGrantEndYear"
          render={({ field: { value, onChange, ...field } }) => (
            <Select
              {...field}
              isPortal
              label={
                <Box display="flex" alignItems="baseline" style={{ gap: 4 }}>
                  <Text as="span" variant="formLabel">
                    Grant End Year
                  </Text>
                  <Text as="span" fontSize="small" color="neutral.500">
                    (Optional)
                  </Text>
                </Box>
              }
              value={dafEndYearOptions.find(
                opt => opt.value === value?.year && opt.type === value?.type,
              )}
              options={endYearOptionsWithAgeLabel}
              onChange={opt => {
                if (opt) {
                  onChange({ year: opt.value, type: opt.type });
                }
              }}
              formVariant={formState.errors.dafGrantEndYear ? 'error' : undefined}
              helperText={(formState.errors.dafGrantEndYear as FieldError)?.message}
            />
          )}
        />
      </Box>

      <Controller
        control={control}
        name="dafGrantAmount"
        render={({ field: { onChange, value, ...field } }) => (
          <InputNumberMask
            {...field}
            label={
              <Box display="flex" alignItems="baseline" style={{ gap: 4 }}>
                <Text as="span" variant="formLabel">
                  Grant Amount
                </Text>
                <Text as="span" fontSize="small" color="neutral.500">
                  (Optional)
                </Text>
              </Box>
            }
            leftIcon="fa fa-dollar-sign"
            innerText="/annually"
            value={value ?? ''}
            allowNegative={false}
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
          />
        )}
      />

      <FundAccount />

      {hasAssetBucketing && <AssetBucketingSelect clientId={clientId} control={control} />}
    </>
  );
};
